import React, { useCallback, useEffect, useState } from "react";
import { FaTimes } from "react-icons/fa";
import { getAuthHeaders } from "../organization/organizationStorage";
import {
  formatAadharDisplay,
  formatAadharDisplayOrFallback,
  formatMobileDisplayOrFallback,
  sanitizeAadharInput,
} from "../restaurant/formatDisplay";
import { useAutoDismissMessage } from "../utils/useAutoDismissMessage";
import "./EatsAdminCreatePage.css";
import "./EatsAdminDashboard.css";

function userHasType(user, typeFilter) {
  if (!typeFilter) return true;
  const wanted = String(typeFilter).trim().toUpperCase();
  const aliases = wanted === "RST" || wanted === "RESTAURANT" ? ["RST", "RESTAURANT"] : [wanted];
  const types = Array.isArray(user?.types)
    ? user.types
    : [user?.type, user?.userType].filter(Boolean);
  return types.some((t) => aliases.includes(String(t || "").trim().toUpperCase()));
}

function looksLikeAadharQuery(value) {
  const trimmed = String(value || "").trim();
  if (!trimmed) return false;
  return /^\d[\d\s]*$/.test(trimmed);
}

const RootAdminUserPicker = ({
  axiosInstance,
  apiPath,
  selectedUserId,
  onSelect,
  onClear,
  toolbarRight = null,
  autoLoad = false,
  requireQuery = false,
  typeFilter = null,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [users, setUsers] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  useAutoDismissMessage(error, setError);

  const fetchUsers = useCallback(
    async (rawQuery = "") => {
      if (!axiosInstance || !apiPath) return;
      const trimmed = String(rawQuery || "").trim();
      const aadharDigits = sanitizeAadharInput(trimmed);
      const isAadhar = looksLikeAadharQuery(trimmed) && Boolean(aadharDigits);

      if (requireQuery && !trimmed) {
        setUsers([]);
        setHasSearched(false);
        setError("Enter a name or Aadhar number to search.");
        return;
      }

      setLoading(true);
      setError("");
      setHasSearched(true);
      try {
        const params = {
          ...(isAadhar ? { aadharNo: aadharDigits } : trimmed ? { name: trimmed } : {}),
          ...(typeFilter ? { type: typeFilter } : {}),
        };
        const res = await axiosInstance.get(apiPath, {
          params,
          headers: getAuthHeaders(),
        });
        const rows = Array.isArray(res?.data?.users) ? res.data.users : [];
        setUsers(typeFilter ? rows.filter((user) => userHasType(user, typeFilter)) : rows);
      } catch (e) {
        setUsers([]);
        setError(e?.response?.data?.message || "Failed to load root admin users.");
      } finally {
        setLoading(false);
      }
    },
    [axiosInstance, apiPath, requireQuery, typeFilter]
  );

  useEffect(() => {
    if (autoLoad) {
      fetchUsers();
    }
  }, [autoLoad, fetchUsers]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(searchQuery);
  };

  const handleClear = () => {
    setSearchQuery("");
    setUsers([]);
    setHasSearched(false);
    setError("");
    onClear?.();
  };

  const isAadharInput = looksLikeAadharQuery(searchQuery);

  return (
    <div className="root-admin-user-picker">
      <div className="root-admin-user-picker__title">
        {typeFilter === "RST" || typeFilter === "RESTAURANT"
          ? "Search restaurant users by name or Aadhar"
          : "Assign existing root admin"}
      </div>
      <form onSubmit={handleSearch} className="root-admin-user-picker__search">
        <div className="eats-admin-input-wrap">
          <input
            type="text"
            className="eats-admin-input"
            placeholder="Search by name or Aadhar Number"
            value={isAadharInput ? formatAadharDisplay(searchQuery) : searchQuery}
            onChange={(e) => {
              const next = e.target.value;
              setSearchQuery(looksLikeAadharQuery(next) ? sanitizeAadharInput(next) : next);
            }}
            maxLength={isAadharInput ? 14 : 80}
            inputMode={isAadharInput ? "numeric" : "text"}
          />
          {searchQuery ? (
            <button
              type="button"
              className="eats-admin-input-clear"
              onClick={handleClear}
              aria-label="Clear search"
            >
              <FaTimes aria-hidden />
            </button>
          ) : null}
        </div>
        <button
          type="submit"
          disabled={loading}
          className="eats-admin-btn eats-admin-btn--primary"
        >
          {loading ? "Searching..." : "Search"}
        </button>
        <button
          type="button"
          onClick={handleClear}
          className="eats-admin-btn eats-admin-btn--secondary"
        >
          Clear
        </button>
        {toolbarRight ? (
          <div className="root-admin-user-picker__search-right">{toolbarRight}</div>
        ) : null}
      </form>

      {error ? <div className="eats-admin-error">{error}</div> : null}

      <div className="eats-admin-table-wrap eats-admin-table-wrap--tall root-admin-user-picker__table">
        <table className="eats-admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Aadhar</th>
              <th>Mobile</th>
              <th>Email</th>
              <th>Types</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan={6} className="eats-admin-table__empty">
                  {loading
                    ? "Searching..."
                    : hasSearched
                      ? typeFilter
                        ? "No restaurant users found."
                        : "No root admin users found."
                      : "Search by name or Aadhar number. Users are not loaded by default."}
                </td>
              </tr>
            ) : (
              users.map((u) => {
                const id = String(u.userId ?? u.user_id ?? "");
                const isSelected = selectedUserId != null && String(selectedUserId) === id;
                const types = Array.isArray(u.types) ? u.types.join(", ") : u.type || "—";
                return (
                  <tr key={id} className={isSelected ? "root-admin-user-picker__row--selected" : undefined}>
                    <td>
                      {u.name || `${u.firstName || ""} ${u.lastName || ""}`.trim() || "—"}
                    </td>
                    <td>{formatAadharDisplayOrFallback(u.aadharNo, "—")}</td>
                    <td style={{ whiteSpace: "nowrap", minWidth: 130 }}>
                      {formatMobileDisplayOrFallback(u.mobile || u.contactNumber, "—")}
                    </td>
                    <td>{u.email || "—"}</td>
                    <td>{types}</td>
                    <td>
                      <button
                        type="button"
                        onClick={() => onSelect?.(u)}
                        className={`eats-admin-btn eats-admin-btn--ghost${isSelected ? " is-active" : ""}`}
                      >
                        {isSelected ? "Selected" : "Select"}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RootAdminUserPicker;
